import { Customer, PlanType } from '../../../admin/modules/NewCustomers/reducer'
import type { ReferralDetailsProps } from '../../../oppsync/components/CoSell/ReferralDetails/ReferralDetails/ReferralDetails'
import {
  LeadStage,
  ReferralStage,
} from '../../../oppsync/components/CoSell/types'
import type { CosellReferralType } from '../../../oppsync/modules/CoSell/actions'
import { useSelector } from '../../../store'
import {
  FUNDING_MANAGEMENT_PRODUCT,
  PartnerType,
  PartnershipTypeValue,
} from '../../modules/partner/action'
import { ProductType } from '../../modules/partner/reducer'
import { CloudType } from '../../modules/types'
import { getReferralType } from '../helperFunctions'
import { isAdminRole, hasAdminRoles } from '../isAdminRole'
import { Role, UserRoleForTable } from '../roles'
import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { AccountData } from '../../modules/accountSelection'
import {
  getAdminPartnerIdFromPathname,
  getHubspotCosellAccountIdFromPath,
  getHubspotOppsyncAccountIdFromState,
  getOppsyncAwsAccountIdFromState,
  getOppsyncAzureAccountIdFromState,
  getPartnerIdFromState,
  getTenantPartnerIdFromState,
} from './partnerStateSelectors'

export {
  getAdminPartnerIdFromPathname,
  getHubspotCosellAccountIdFromPath,
  getHubspotOppsyncAccountIdFromState,
  getOppsyncAwsAccountIdFromState,
  getOppsyncAzureAccountIdFromState,
  getPartnerIdFromState,
  getTenantPartnerIdFromState,
} from './partnerStateSelectors'

const emptyCustomerForm: Customer = {
  companyName: '',
  quotationLink: '',
  internalNotes: '',
  crmType: null,
  numberOfSeats: 100,
  isInternal: false,
  includeLabraBot: false,
  users: [
    {
      email: '',
      role: 'PARTNER_OWNER' as UserRoleForTable,
    },
  ],
  subscribedProducts: {},
  partnershipType: [],
  plan: 'free_tier',
}

export const useUserType = () => {
  return useSelector<PartnerType>(state => {
    const authRoles: string[] = state.authorization?.roles ?? []
    if (authRoles.length > 0) {
      return hasAdminRoles(authRoles as Role[])
        ? PartnerType.Admin
        : PartnerType.User
    }
    return isAdminRole(state.userProfile.userProfile)
      ? PartnerType.Admin
      : PartnerType.User
  })
}

/**
 * Returns the tenant/partner ID (the organization's ID, not the selected account ID)
 */
export const useTenantPartnerId = () => {
  const userType = useUserType()
  const pathname = useSafeLocation()
  return useSelector<string | undefined>(state =>
    getTenantPartnerIdFromState(state, userType, pathname)
  )
}

/**
 * Safe hook to get location pathname
 */
const useSafeLocation = (): string => {
  try {
    const location = useLocation()
    return location.pathname
  } catch (error) {
    if (typeof window !== 'undefined' && window.location) {
      return window.location.pathname
    }
    return ''
  }
}

/**
 * Returns the currently selected account ID based on the active product and cloud
 */
export const useAccountId = (options?: {
  product?: ProductType
  cloud?: CloudType
}) => {
  const userType = useUserType()
  const pathname = useSafeLocation()
  const { accountId: accountIdFromParams } = useParams<{ accountId: string }>()

  return useSelector<string | undefined>(state => {
    if (options?.product && options?.cloud) {
      if (accountIdFromParams) {
        return accountIdFromParams
      }

      const accounts = state.accountSelection.availableAccounts
      const accountData = (Array.isArray(accounts) ? accounts : []).filter(
        (account: AccountData) =>
          account?.product?.toLowerCase() === options.product
      )
      return (
        accountData?.find(
          (account: AccountData) => account?.cloud === options.cloud
        )?.id || ''
      )
    }

    return getPartnerIdFromState(state, pathname, userType)
  })
}

export const useOppsyncAwsAccountId = () => {
  return useSelector<string | undefined>(state =>
    getOppsyncAwsAccountIdFromState(state)
  )
}

export const useOppsyncAzureAccountId = () => {
  return useSelector<string | undefined>(state =>
    getOppsyncAzureAccountIdFromState(state)
  )
}

export const useCurrentProduct = (defaultProduct: ProductType) => {
  const userType = useUserType()
  return useSelector<ProductType>(
    state => state.AccountData[userType]?.currentProduct || defaultProduct
  )
}

export const customerFormData = () => {
  const userType = useUserType()
  const partnerData = useSelector(
    state => state?.AccountData[userType]?.partnerData
  )

  return useMemo<Customer>(
    () => ({
      companyName: partnerData?.companyName || emptyCustomerForm.companyName,
      quotationLink:
        partnerData?.quotationLink || emptyCustomerForm.quotationLink,
      internalNotes:
        partnerData?.internalNotes || emptyCustomerForm.internalNotes,
      crmType: partnerData?.crmType || emptyCustomerForm.crmType,
      numberOfSeats:
        partnerData?.numberOfSeats || emptyCustomerForm.numberOfSeats,
      isInternal: partnerData?.isInternal || emptyCustomerForm.isInternal,
      includeLabraBot:
        partnerData?.includeLabraBot || emptyCustomerForm.includeLabraBot,
      users: partnerData?.users || emptyCustomerForm.users,
      subscribedProducts:
        partnerData?.subscribedProducts || emptyCustomerForm.subscribedProducts,
      partnershipType:
        (partnerData?.partnershipType as PartnershipTypeValue[]) ||
        emptyCustomerForm.partnershipType,
      plan:
        (partnerData?.plan?.toLowerCase() as PlanType) ||
        emptyCustomerForm.plan,
      fundingEnabled:
        (
          partnerData?.activeProductsWithCloud as string[] | undefined
        )?.includes(FUNDING_MANAGEMENT_PRODUCT) ?? false,
    }),
    [partnerData]
  )
}

export const getCosellFormData = () => {
  return useSelector<ReferralDetailsProps | undefined>(state => {
    let cosellReferralType: CosellReferralType
    const typeFromOpportunity =
      state.referralFormCreation.referral?.referralMetadata?.referralType
    const typeFromLead =
      state.leadReferralForm.referral?.referralMetadata?.referralType
    if (typeFromOpportunity) {
      cosellReferralType = 'opportunity'
    } else if (typeFromLead) {
      cosellReferralType = 'lead'
    } else {
      return undefined
    }
    if (cosellReferralType === 'opportunity') {
      const referral = state.referralFormCreation.referral
      if (!referral || !referral.referralObjectTransformed) {
        return undefined
      }
      return {
        referralType: getReferralType(
          'opportunity',
          referral.referralObjectTransformed.referredBy || 'AWS Referral'
        ),
        referralId: referral.referralMetadata.referralId || '',
        referredBy: referral.referralObjectTransformed.referredBy,
        acceptanceStatus: referral.referralObjectTransformed.acceptanceStatus,
        targetCloseDate: referral.referralObjectTransformed?.targetCloseDate,
        acceptByDate: referral.referralObjectTransformed?.acceptByDate,
        lastSyncStatus: referral.referralMetadata?.lastSyncStatus,
        isDraft: referral.referralMetadata.referralStatus === 'draft',
        expectedMonthlyAwsRevenue:
          referral.referralObjectTransformed.expectedMonthlyAwsRevenue + '',
        cloudProviderAcceptanceStatus:
          referral.referralObjectTransformed.cloudProviderAcceptanceStatus,
        stage:
          referral.referralObjectTransformed.stage ||
          ('Prospect' as ReferralStage),
        crmUniqueIdentifier:
          referral.referralObjectTransformed.crmUniqueIdentifier,
        engagementScore: referral.referralObjectTransformed.engagementScore,
        createdAt: referral.referralObjectTransformed.createdDate,
        lastModifiedDate: referral.referralObjectTransformed.lastModifiedDate,
        lastUpdatedBy: referral.referralObjectTransformed.lastUpdatedBy,
      }
    } else {
      const referral = state.leadReferralForm.referral
      return {
        referralType: 'incoming-leads',
        referralId: referral?.referralObjectTransformed?.id || '',
        referredBy: referral?.referralObjectTransformed?.referredBy,
        acceptanceStatus: referral?.referralObjectTransformed?.acceptanceStatus,
        targetCloseDate: referral?.referralObjectTransformed?.labraAcceptByDate,
        lastSyncStatus: referral?.referralMetadata?.lastSyncStatus,
        campaignName: referral?.referralObjectTransformed?.campaignName,
        stage:
          referral?.referralObjectTransformed?.awsLeadStatus ||
          ('Prospect' as LeadStage),
        isDraft: referral?.referralMetadata.referralStatus === 'draft',
        crmUniqueIdentifier:
          referral?.referralObjectTransformed?.partnerCRMLeadId,
      }
    }
  })
}
